/**
 * services/EconomicCalendarSettingsService.ts
 * Economic calendar settings management service
 *
 * Provides singleton settings for economic calendar configuration.
 * Users define API integration preferences, country filters, and impact level filtering.
 */

import { BaseService } from './BaseService';
import type { EconomicCalendarSettings } from '@/types/schema/EconomicCalendarSettings';

/** Valid impact level values */
type ImpactLevel = 'low' | 'medium' | 'high';

/** Canonical ordering for impact levels */
const IMPACT_LEVEL_ORDER: readonly ImpactLevel[] = ['low', 'medium', 'high'] as const;

/** Set for O(1) validation of impact level values */
const VALID_IMPACT_LEVELS = new Set<string>(IMPACT_LEVEL_ORDER);

/**
 * Service for managing economic calendar settings
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized methods for getting or creating default settings.
 */
export class EconomicCalendarSettingsService extends BaseService<EconomicCalendarSettings> {
  constructor() {
    super('economic_calendar_settings');
  }

  /**
   * Normalize impact levels array: filter invalid values, dedupe, and order canonically.
   *
   * @param levels - Raw impact levels array (may contain invalid/duplicate values)
   * @returns Normalized array in canonical order ['low', 'medium', 'high']
   * @private
   */
  private normalizeImpactLevels(levels: unknown): ImpactLevel[] {
    // Guard against corrupted data (null, undefined, non-array)
    if (!Array.isArray(levels)) {
      return [];
    }
    // Filter to valid values only, dedupe via Set, then sort by canonical order
    const validLevels = new Set(
      levels.filter((level): level is ImpactLevel => VALID_IMPACT_LEVELS.has(level as string))
    );
    return IMPACT_LEVEL_ORDER.filter(level => validLevels.has(level));
  }

  /**
   * Migrate from legacy minimumImpactLevel field to new impactLevels array.
   *
   * @param legacyValue - The legacy minimumImpactLevel value (or undefined if missing)
   * @returns Equivalent impactLevels array
   * @private
   */
  private migrateFromMinimumImpactLevel(
    legacyValue: 'low' | 'medium' | 'high' | undefined
  ): ImpactLevel[] {
    switch (legacyValue) {
      case 'high':
        return ['high'];
      case 'medium':
        return ['medium', 'high'];
      case 'low':
      default:
        // 'low' or missing/undefined defaults to all levels
        return ['low', 'medium', 'high'];
    }
  }

  /**
   * Get default economic calendar settings
   *
   * @returns Default settings object (without id, timestamps, or sync fields)
   * @private
   */
  private getDefaultSettings(): Omit<
    EconomicCalendarSettings,
    'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'deletedByDevice'
  > {
    return {
      // API integration enabled by default
      apiEnabled: true,
      // Empty array - show all countries (no filtering)
      countryFilters: [],
      // Show all impact levels by default
      impactLevels: ['low', 'medium', 'high'],
    };
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving economic calendar settings.
   * On first use, it creates a settings record with sensible defaults.
   * Handles backward compatibility migration from legacy minimumImpactLevel field.
   *
   * @returns Promise resolving to EconomicCalendarSettings
   */
  async getOrCreateDefaultSettings(): Promise<EconomicCalendarSettings> {
    try {
      // Check if settings already exist (getAll excludes tombstones)
      const allSettings = await this.getAll();

      if (allSettings.length > 0) {
        // Return first settings record (singleton semantics)
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }

        // Check for legacy field and migrate if needed
        const settingsRecord = firstSetting as EconomicCalendarSettings & {
          minimumImpactLevel?: 'low' | 'medium' | 'high';
        };

        if (!('impactLevels' in settingsRecord) || settingsRecord.impactLevels === undefined) {
          // Migrate from legacy minimumImpactLevel
          const migratedLevels = this.migrateFromMinimumImpactLevel(
            settingsRecord.minimumImpactLevel
          );

          // Save migrated settings
          const updatedSettings = await this.update(settingsRecord.id, {
            impactLevels: migratedLevels,
          });

          return updatedSettings;
        }

        return firstSetting;
      }

      // No settings exist, create default
      const defaultSettings = this.getDefaultSettings();
      const createdSettings = await this.add(defaultSettings);

      return createdSettings;
    } catch (error) {
      console.error('Failed to get or create economic calendar settings:', error);
      throw new Error('Failed to initialize economic calendar settings');
    }
  }

  /**
   * Update settings with normalization for impactLevels field.
   *
   * Overrides BaseService.update() to normalize impactLevels when present
   * in the changes object. This ensures consistent storage (canonical order,
   * no duplicates, no invalid values).
   *
   * @param id - Settings record ID
   * @param changes - Partial settings to update
   * @returns Promise resolving to updated EconomicCalendarSettings
   */
  override async update(
    id: string,
    changes: Partial<EconomicCalendarSettings>
  ): Promise<EconomicCalendarSettings> {
    // Normalize impactLevels if present in changes
    if ('impactLevels' in changes && changes.impactLevels !== undefined) {
      changes = {
        ...changes,
        impactLevels: this.normalizeImpactLevels(changes.impactLevels),
      };
    }

    return super.update(id, changes);
  }

  /**
   * Get current economic calendar settings
   *
   * Alias for getOrCreateDefaultSettings() for semantic clarity.
   *
   * @returns Promise resolving to current EconomicCalendarSettings
   */
  async getCurrentSettings(): Promise<EconomicCalendarSettings> {
    return this.getOrCreateDefaultSettings();
  }
}

// Export singleton instance for use throughout the application
export const economicCalendarSettingsService = new EconomicCalendarSettingsService();
